import { useEffect, useRef, useState } from 'react';
import { Download, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, getTrialId } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export function BatchDownload({ projectId, readyCount, compact = false }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false), [bytes, setBytes] = useState(0);
  const [error, setError] = useState(''), [complete, setComplete] = useState('');
  const request = useRef(null);
  useEffect(() => {
    setBusy(false); setError(''); setComplete(''); setBytes(0);
    return () => { request.current?.abort(); request.current = null; };
  }, [projectId, user?.id]);
  const download = async () => {
    if (request.current || !readyCount) return;
    const controller = new AbortController(); request.current = controller;
    setBusy(true); setBytes(0); setError(''); setComplete('');
    try {
      const response = await api.get(`/projects/${projectId}/download-zip`, {
        params: { trial_id: getTrialId() }, responseType: 'blob', signal: controller.signal,
        onDownloadProgress: (event) => { if (!controller.signal.aborted) setBytes(event.loaded); },
      });
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(response.data), link = document.createElement('a');
      link.href = url;
      link.download = response.headers['content-disposition']?.match(/filename="([^"]+)"/)?.[1] || 'clippedup-clips.zip';
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setComplete(`ZIP ready · ${response.headers['x-clip-count'] || readyCount} clips. Check your downloads.`);
    } catch (failure) {
      if (!controller.signal.aborted) {
        let message = 'The ZIP download was interrupted. Please retry; your clips are safe.';
        try { const body = JSON.parse(await failure.response.data.text()); if (typeof body.detail === 'string') message = body.detail; } catch {}
        if (!controller.signal.aborted) setError(message);
      }
    } finally {
      if (request.current === controller) { request.current = null; setBusy(false); }
    }
  };
  const id = `batch-download-${projectId}`;
  return <div data-testid={`${id}-container`} className="min-w-0 space-y-2">
    <div className="flex flex-wrap items-center gap-2">
      <Button data-testid={id} disabled={busy || !readyCount} onClick={download} variant="outline" size={compact ? 'sm' : 'default'} aria-label="Download all ready clips as a ZIP file" className="rounded-full border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10">
        {busy ? <Loader2 size={15} className="animate-spin motion-reduce:animate-none" /> : <Download size={15} />}{busy ? 'Downloading ZIP…' : 'Download all (ZIP)'}
      </Button>
      {busy && <Button data-testid={`${id}-cancel`} variant="ghost" size="sm" onClick={() => { request.current?.abort(); }} className="text-xs text-copy-400"><X size={13} /> Cancel</Button>}
    </div>
    <p data-testid={`${id}-status`} role="status" aria-live="polite" className="text-xs text-copy-500">{busy ? bytes ? `${(bytes / 1048576).toFixed(1)} MB received · keep this page open` : 'Collecting ready clips…' : complete || `${readyCount} ready · includes clips finished when the download starts`}</p>
    {error && <p data-testid={`${id}-error`} role="alert" className="max-w-sm text-xs text-amber-500">{error}</p>}
  </div>;
}
